import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Button from '../../components/Button';
import { ArrowLeft, Save, ClipboardList } from 'lucide-react';
import apiService from '../../services/apiService';
import { toLocalDateInput } from '../../utils/formatters';

interface Request {
  _id: string;
  title: string;
  description: string;
  type: 'installation' | 'intervention';
  serviceTypeId: { _id: string; name: string; category: string };
  priority: 'low' | 'normal' | 'high' | 'urgent';
  status: 'pending' | 'assigned' | 'in-progress' | 'completed' | 'cancelled' | 'quoted';
  address: { street: string; city: string; postalCode: string };
  estimatedBudget?: number;
  preferredDate?: string;
  notes?: string;
  attachments?: string[];
  clientId: { _id: string; firstName: string; lastName: string; company?: string } | null;
  assignedTechnician?: { _id: string; firstName: string; lastName: string };
  createdAt: string;
}

interface ServiceType {
  _id: string;
  name: string;
  category: string;
}

interface Client {
  _id: string;
  firstName: string;
  lastName: string;
  company?: string;
}

const EditRequest: React.FC = () => {
  const params = useParams<{ id: string }>();
  const requestId = (params as any)?.id || (params as any)?.requestId;
  const navigate = useNavigate();
  const [request, setRequest] = useState<Request | null>(null);
  const [formData, setFormData] = useState<Partial<Request>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [clients, setClients] = useState<Client[]>([]);

 useEffect(() => {
  const fetchData = async () => {
    if (!requestId) {
      setError('Request ID is missing.');
      setIsLoading(false);
      return;
    }
    try {
      // 1) Récupération demande
      const raw = await apiService.requests.getById(requestId);
      // Tolérer plusieurs shapes: {data:{request}}, {request}, ou l'objet direct
      const requestObj =
        raw?.data?.request ??
        raw?.request ??
        raw;

      console.log('requestResponse (normalized):', requestObj);

      if (!requestObj?._id) {
        throw new Error('Bad response shape for GET /requests/:id');
      }

      // 2) Normaliser l’adresse pour l’UI d’édition
      // Le back renvoie address.full (string). On découpe au mieux en street/city/postalCode.
      const full = requestObj.address?.full || '';
      const parts = full.split(',').map((s: string) => s.trim());
      const [street = '', city = '', postalCode = ''] = parts;

      setRequest(requestObj);
      setFormData({
        title: requestObj.title,
        description: requestObj.description,
        // ⚠️ Ne garde pas "type" ici: le serveur recalcule à partir de serviceTypeId
        serviceTypeId: requestObj.serviceTypeId?._id || '', // stocker l'ID string
        priority: requestObj.priority,
        status: requestObj.status,
        address: { street, city, postalCode }, // pour l’édition UI
        estimatedBudget: requestObj.estimatedBudget,
        preferredDate: requestObj.preferredDate
          ? new Date(requestObj.preferredDate).toISOString().split('T')[0]
          : '',
        notes: requestObj.notes,
        attachments: requestObj.attachments,
        clientId: requestObj.clientId?._id || '', // stocker l'ID string
      });

      // 3) Service types
      const stRes = await apiService.serviceTypes.getAll();
      const serviceTypesItems = stRes?.items || [];
      setServiceTypes(Array.isArray(serviceTypesItems) ? serviceTypesItems : []);
      console.log('Fetched serviceTypes:', serviceTypesItems); // Add this line

      // 4) Clients
      const clientsRes = await apiService.users.getAll({ role: 'client' });
      const clientItems = clientsRes?.items || [];
      setClients(Array.isArray(clientItems) ? clientItems : []);
      console.log('Fetched clients:', clientItems); // Add this line

    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError('Failed to load request details or related data.');
    } finally {
      setIsLoading(false);
    }
  };

  fetchData();
}, [requestId]);


  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
  const { name, value } = e.target;

  if (name === 'serviceTypeId') {
    // on stocke l'ID string (le backend attend un MongoId)
    setFormData({ ...formData, serviceTypeId: value as any });
    return;
  }

  if (name === 'clientId') {
    // idem: on stocke l'ID string
    setFormData({ ...formData, clientId: value as any });
    return;
  }

  if (name === 'street' || name === 'city' || name === 'postalCode') {
    setFormData({
      ...formData,
      address: { ...(formData.address as any), [name]: value },
    });
    return;
  }

  if (name === 'estimatedBudget') {
    setFormData({ ...formData, estimatedBudget: value === '' ? undefined : parseFloat(value) });
    return;
  }

  setFormData({ ...formData, [name]: value });
};

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!requestId) return;

    setIsSaving(true);
    setError(null);
    try {
      const dataToSend = {
        ...formData,
        serviceTypeId: formData.serviceTypeId,
        clientId: formData.clientId?._id,
        'address.street': formData.address?.street,
        'address.city': formData.address?.city,
        'address.postalCode': formData.address?.postalCode,
      };
      delete dataToSend.address; // Remove the nested address object
      await apiService.requests.update(requestId, dataToSend);
      navigate(`/admin/requests/view/${requestId}`);
    } catch (err) {
      console.error('Failed to update request:', err);
      setError('Failed to update request. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <p className="text-lg text-gray-600">Chargement des détails de la demande...</p>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <p className="text-lg text-error-600">Erreur: {error}</p>
        </div>
      </Layout>
    );
  }

  if (!request || !formData) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <p className="text-lg text-gray-600">Demande non trouvée ou données manquantes.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 pb-24">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4 flex items-center text-primary-600">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour aux Détails de la Demande
            </Button>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <ClipboardList className="w-8 h-8 mr-3 text-primary-600" />
              Modifier la Demande: {request.title}
            </h1>
            <p className="text-gray-600 mt-2">
              Mettez à jour les informations de la demande de service #{request._id}
            </p>
          </div>
          
        </div>

        {/* Edit Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">Titre</label>
              <input
                type="text"
                name="title"
                id="title"
                value={formData.title || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
            
            <div>
              <label htmlFor="serviceTypeId" className="block text-sm font-medium text-gray-700">Service</label>
              <select
                name="serviceTypeId"
                id="serviceTypeId"
                value={formData.serviceTypeId?._id || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              >
                <option value="">Sélectionner un service</option>
                {serviceTypes.map((service) => (
                  <option key={service._id} value={service._id}>
                    {service.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="clientId" className="block text-sm font-medium text-gray-700">Client</label>
              <select
                name="clientId"
                id="clientId"
                value={formData.clientId?._id || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              >
                <option value="">Sélectionner un client</option>
                {clients.map((client) => (
                  <option key={client._id} value={client._id}>
                    {client.firstName} {client.lastName} {client.company ? `(${client.company})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="priority" className="block text-sm font-medium text-gray-700">Priorité</label>
              <select
                name="priority"
                id="priority"
                value={formData.priority || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              >
                <option value="">Sélectionner une priorité</option>
                <option value="low">Faible</option>
                <option value="normal">Normale</option>
                <option value="high">Élevée</option>
                <option value="urgent">Urgente</option>
              </select>
            </div>
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700">Statut</label>
              <select
                name="status"
                id="status"
                value={formData.status || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              >
                <option value="">Sélectionner un statut</option>
                <option value="pending">En attente</option>
                <option value="assigned">Assignée</option>
                <option value="in-progress">En cours</option>
                <option value="completed">Terminée</option>
                <option value="cancelled">Annulée</option>
                <option value="quoted">Devis envoyé</option>
              </select>
            </div>
            <div>
              <label htmlFor="estimatedBudget" className="block text-sm font-medium text-gray-700">Budget Estimé (€)</label>
              <input
                type="number"
                name="estimatedBudget"
                id="estimatedBudget"
                value={formData.estimatedBudget || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label htmlFor="preferredDate" className="block text-sm font-medium text-gray-700">Date Préférée</label>
              <input
                type="date"
                name="preferredDate"
                id="preferredDate"
                value={formData.preferredDate || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                name="description"
                id="description"
                rows={4}
                value={formData.description || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              ></textarea>
            </div>
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700">Notes Additionnelles</label>
              <textarea
                name="notes"
                id="notes"
                rows={3}
                value={formData.notes || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              ></textarea>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label htmlFor="street" className="block text-sm font-medium text-gray-700">Rue</label>
              <input
                type="text"
                name="street"
                id="street"
                value={formData.address?.street || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700">Ville</label>
              <input
                type="text"
                name="city"
                id="city"
                value={formData.address?.city || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
            <div>
              <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700">Code Postal</label>
              <input
                type="text"
                name="postalCode"
                id="postalCode"
                value={formData.address?.postalCode || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
          </div>

          {error && <p className="text-error-600 text-sm mt-4">{error}</p>}

          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-10">
            <div className="max-w-4xl mx-auto flex justify-end space-x-3">
              <Button type="button" variant="secondary" onClick={() => navigate(-1)}>
                Annuler
              </Button>
              <Button type="submit" disabled={isSaving}>
                <Save className="w-5 h-5 mr-2" />
                {isSaving ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default EditRequest;
